/** "Salty meal or ate out": logs the salt that database foods leave out. */
import { useState, type ReactElement } from 'react';
import { formatDayLong, timestampForDay } from '../lib/dates';
import { MEAL_HOUR, MEAL_LABEL, mealTypeForNow } from '../lib/labels';
import { errorMessage } from '../lib/logger';
import { COOKING_SALT_TSP, MAX_SALT_ENTRY_MG, RESTAURANT_MEAL_SODIUM_MG, SALT_CITATIONS, saltMealInsert, saltTspToSodiumMg, type SaltChoice } from '../lib/salt';
import { MEAL_TYPES, type MealType } from '../lib/types';
import { useApp } from '../state/app';
import { Icon } from './Icon';
import { Modal, Spinner } from './ui';

const mg = (v: number): string => `${v.toLocaleString('en-US')} mg`;
const near10 = (v: number): number => Math.round(v / 10) * 10;

const CHOICES: ReadonlyArray<{ id: SaltChoice; label: string; hint: string }> = [
  { id: 'restaurant', label: 'Restaurant or takeout meal', hint: `About ${mg(RESTAURANT_MEAL_SODIUM_MG)} sodium, the average fast-food meal in a US study that checked receipts.` },
  { id: 'cooking', label: 'Salted while cooking', hint: `1/4 tsp of table salt, about ${mg(near10(saltTspToSodiumMg(COOKING_SALT_TSP)))} sodium.` },
  { id: 'custom', label: 'Other amount', hint: 'Type the sodium, for example from the restaurant’s nutrition info.' },
];

export function SaltModal({ defaultMealType, onClose }: { defaultMealType?: MealType; onClose: () => void }): ReactElement {
  const { source, day, today, toast, bump } = useApp();
  const [choice, setChoice] = useState<SaltChoice>('restaurant');
  const [customText, setCustomText] = useState<string>('');
  const [mealType, setMealType] = useState<MealType>(defaultMealType ?? mealTypeForNow());
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    const row = saltMealInsert({
      choice, mealType, localDate: day,
      customMg: customText.trim() === '' ? null : Number(customText),
      eatenAt: day === today ? new Date().toISOString() : timestampForDay(day, MEAL_HOUR[mealType]),
    });
    if (row === null) { setError(`Enter the sodium in mg, from 1 to ${MAX_SALT_ENTRY_MG.toLocaleString('en-US')}.`); return; }
    setBusy(true); setError(null);
    try {
      await source.insertMeal(row);
      toast(`Added ${mg(row.sodium_mg ?? 0)} sodium to ${MEAL_LABEL[mealType]}`);
      bump();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Salty meal or ate out" onClose={onClose} width={500} footer={
      <>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>{busy ? <Spinner /> : <Icon name="check" size={16} />} Add salt</button>
      </>
    }>
      <div className="stack" style={{ gap: 14 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>
          Foods from the database are measured without added salt, so salt from a restaurant kitchen or your own cooking never reaches your sodium total or the water-weight estimate. This adds it as its own diary entry.
        </p>
        <div className="stack" style={{ gap: 10 }} role="radiogroup" aria-label="How much salt">
          {CHOICES.map((c) => (
            <label key={c.id} className="check" style={{ alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="radio" name="salt-choice" checked={choice === c.id} onChange={() => { setChoice(c.id); setError(null); }} style={{ marginTop: 3 }} />
              <span>{c.label}<span className="faint" style={{ display: 'block', fontSize: 12.5 }}>{c.hint}</span></span>
            </label>
          ))}
        </div>
        {choice === 'custom' && (
          <label className="field"><span>Sodium</span>
            <div className="input-suffix">
              <input className="input" type="number" inputMode="numeric" min={1} max={MAX_SALT_ENTRY_MG} step={10} value={customText} onChange={(e) => setCustomText(e.target.value)} autoFocus />
              <em>mg</em>
            </div>
            <small>1 tsp of table salt is about {mg(near10(saltTspToSodiumMg(1)))} sodium.</small>
          </label>
        )}
        <label className="field"><span>Meal</span>
          <select className="input" value={mealType} onChange={(e) => setMealType(e.target.value as MealType)}>
            {MEAL_TYPES.map((m) => <option key={m} value={m}>{MEAL_LABEL[m]}</option>)}
          </select>
        </label>
        <p className="faint" style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
          Goes to {day === today ? 'today' : formatDayLong(day)}. A meal logged by description or photo may already include restaurant salt, so skip this for those.
        </p>
        {error !== null && <div className="note err">{error}</div>}
        <details>
          <summary className="cite" style={{ cursor: 'pointer', fontWeight: 700 }}>Where these numbers come from</summary>
          <ol className="cite" style={{ paddingLeft: 18, margin: '6px 0 0' }}>
            {SALT_CITATIONS.map((c) => <li key={c.key}>{c.text} <a href={c.url} target="_blank" rel="noreferrer">link</a></li>)}
          </ol>
        </details>
      </div>
    </Modal>
  );
}
